import { type ChangeEvent, useRef } from 'react'
import { Box, IconButton, CircularProgress, Tooltip } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import ErrorIcon from '@mui/icons-material/Error'
import { useSendMessage } from '../hooks'
import { AttachmentChips } from './AttachmentChips'
import { MessageEditor } from './MessageEditor'

export type TChatInputProps = {
  url: string
  placeholder?: string
  disabled?: boolean
  framed?: boolean
  expanded?: boolean
  onSent: () => Promise<void> | void
}

export const ChatInput = ({
  url,
  placeholder,
  disabled = false,
  framed = true,
  expanded = false,
  onSent,
}: TChatInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const {
    message,
    attachments,
    isSending,
    hasError,
    canSend,
    sendMessage,
    addAttachments,
    removeAttachment,
    updateMessage,
  } = useSendMessage({ url, onSuccess: onSent })

  const onFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    addAttachments(Array.from(event.target.files ?? []))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const getSendButtonIcon = () => {
    if (isSending) {
      return <CircularProgress size={20} />
    }
    if (hasError) {
      return <ErrorIcon fontSize="small" />
    }
    return <SendIcon fontSize="small" />
  }

  const handleEnter = () => {
    if (canSend && !isSending) {
      sendMessage()
    }
  }

  return (
    <Box sx={framed ? { p: 2, borderTop: 1, borderColor: 'divider' } : undefined}>
      <AttachmentChips attachments={attachments} onRemove={removeAttachment} />

      <MessageEditor
        value={message}
        placeholder={placeholder}
        disabled={isSending || disabled}
        expanded={expanded}
        onChange={updateMessage}
        onEnter={handleEnter}
        actions={
          <>
            <Tooltip title="Attach files">
              <span>
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSending || disabled}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <IconButton
              size="small"
              onClick={sendMessage}
              disabled={isSending || !canSend || disabled}
              color={hasError ? 'error' : 'primary'}
            >
              {getSendButtonIcon()}
            </IconButton>
          </>
        }
      />

      <Box
        component="input"
        ref={fileInputRef}
        type="file"
        multiple
        sx={{ display: 'none' }}
        onChange={onFileSelect}
        accept="*/*"
      />
    </Box>
  )
}
